import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

// zIndex and panelStyle added Session 79.14 for #250 / #148 Split C. BOTH ARE
// OPTIONAL AND BOTH DEFAULT TO EXACTLY WHAT THIS COMPONENT DID BEFORE, so every
// existing caller in the app is untouched - that is the whole point of the
// defaults and it should stay true.
//
// WHY zIndex EXISTS. PurchasingQueuePage.js stacks three depths on purpose: on a
// phone the detail sheet sits UNDER the action sheets, and the cancel
// confirmation sits above both. They are rendered as siblings, so a single fixed
// depth would decide their order by DOM position instead - which puts the detail
// sheet on top of the Mark Ordered sheet, exactly backwards. Converting those
// sheets was blocked on this and on nothing else.
//
// This is NOT #148 Split B and does not pre-empt it. Split B's job is a real
// NAMED z-index scale for the whole app; this is a number a caller may pass so
// that five sheets do not have to wait for that design. When the scale lands,
// these call sites take names instead of literals and this prop keeps working.
//
// WHY panelStyle EXISTS. Locked rule 3 is about `position: fixed` and the portal
// - those stay owned here and are not overridable. The PANEL's own chrome is the
// caller's business, and it has to be: two of the sheets that needed converting
// are scrolling columns with sticky headers, which need their own maxHeight,
// flex direction and overflow. Without this they would either overflow the
// viewport or gain a second nested scrollbar.
export default function Overlay({ variant = 'modal', onClose, children, maxWidth, zIndex = 3000, panelStyle }) {
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'Escape' && onClose) onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const isSheet = variant === 'sheet';
  // #417 D24, Session 79.15b. The third named variant. NOT new work: the app
  // already hand-rolls a right-edge panel three times -- 360px in
  // TransactionDetailPanel, 340px in the tool detail pane and 420px in the
  // printer registry's slideover -- each with its own fixed positioning, which
  // is three violations of locked rule 3 that nobody set out to commit.
  // Naming it is what stops the fourth. #164 and #148 Split B migrate the
  // existing three at the Tools sprint; this only makes the destination exist.
  const isDrawer = variant === 'drawer';

  const backdropStyle = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    zIndex,
    display: 'flex',
    // A drawer fills the height and sits against the right edge, so it stretches
    // on the cross axis and justifies to the end. Sheet and modal are untouched.
    alignItems: isDrawer ? 'stretch' : (isSheet ? 'flex-end' : 'center'),
    justifyContent: isDrawer ? 'flex-end' : 'center',
  };

  const drawerStyle = {
    background: '#fff',
    // 360 is the median of the three it replaces (340, 360, 420) and the one
    // that appears twice. A caller with a reason overrides it with maxWidth.
    width: maxWidth || 360,
    maxWidth: '100vw',
    height: '100%',
    // Capped and scrollable for the same reason the modal now is -- #554. A
    // drawer is full-height by construction, so its content is the thing that
    // overflows, and a clipped confirm button here would be the same defect
    // in a new variant.
    maxHeight: '100vh',
    overflowY: 'auto',
    overflowX: 'hidden',
    // Only the leading corners round. The trailing edge meets the window.
    borderRadius: '14px 0 0 14px',
    // Longhand throughout, per the sheet branch's note: this component switches
    // variant at runtime and React warns when a shorthand and a longhand for the
    // same value are mixed across rerenders.
    borderTop: '0.5px solid #ddd',
    borderLeft: '0.5px solid #ddd',
    borderRight: 'none',
    borderBottom: '0.5px solid #ddd',
  };

  const contentStyle = isDrawer
    ? drawerStyle
    : isSheet
    ? {
        background: '#fff',
        borderRadius: '16px 16px 0 0',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        // Longhand on both variants, deliberately. #224 made this the first component
        // to switch variant at runtime (isMobile flips it on a window resize), and
        // React warns when a shorthand `border` and a longhand `borderBottom` are set
        // on the same element across rerenders - "don't mix shorthand and
        // non-shorthand properties for the same value". Longhand everywhere means
        // nothing is being overridden, so the sheet-to-modal flip is warning-free.
        borderTop: '0.5px solid #ddd',
        borderLeft: '0.5px solid #ddd',
        borderRight: '0.5px solid #ddd',
        borderBottom: 'none',
      }
    : {
        background: '#fff',
        borderRadius: 14,
        width: maxWidth || 620,
        maxWidth: '95vw',
        // #554. This branch had NO maxHeight and `overflow: 'hidden'`, four lines
        // from the sheet variant that has capped and scrolled since it was written.
        // A modal is centred in a flex backdrop, so content taller than the viewport
        // overflows EQUALLY in both directions and `hidden` clips it instead of
        // offering a scrollbar - the submit button simply is not on the screen, and
        // there is no keyboard route to a clipped element either. Colby hit it on a
        // 16:9 display. Twelve callers had independently invented the same
        // workaround and two wrote down why, which is what makes it the COMPONENT's
        // defect rather than theirs.
        //
        // LONGHAND, for the reason the sheet branch gives above: this component
        // flips variant at runtime, and a shorthand `overflow` on one branch beside
        // a longhand `overflowY` on the other is the mix React warns about. overflowX
        // stays hidden so the rounded corners still clip, which is all the shorthand
        // was ever doing here.
        maxHeight: '90vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        borderTop: '0.5px solid #ddd',
        borderLeft: '0.5px solid #ddd',
        borderRight: '0.5px solid #ddd',
        borderBottom: '0.5px solid #ddd',
      };

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget && onClose) onClose();
  }

  // panelStyle MERGES OVER the variant default rather than replacing it, so a
  // caller overriding maxHeight does not accidentally lose the background, the
  // border radius or the borders that make it look like the rest of the app.
  const merged = panelStyle ? { ...contentStyle, ...panelStyle } : contentStyle;

  // #756, Session 79.22b.5.5 -- THE CORNERS STAY ROUND WHILE THE PANEL SCROLLS.
  // Since #554 a tall panel scrolls inside itself, and on Windows the browser
  // draws its classic scrollbar as a square strip inside the panel, over the
  // right-hand corners: border-radius with overflow-x hidden rounds the content,
  // not the scrollbar. Clipping the panel to its own rounded shape draws the
  // scrollbar inside the curve. Taken from whichever corner the panel ends up
  // with, so the purchasing sheets' 16px (panelStyle) clips at 16, not 14; a
  // caller that sets its own clipPath keeps it.
  const radius = merged.borderRadius;
  const mergedPanelStyle = radius && !merged.clipPath
    ?{ ...merged, clipPath: `inset(0 round ${typeof radius === 'number' ? `${radius}px` : radius})` }
    : merged;

  return createPortal(
    <div style={backdropStyle} onClick={handleBackdropClick}>
      <div style={mergedPanelStyle} onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>,
    document.body
  );
}